import { Box, Text, useTranslation, Button, Flex, InlineNotification, Icon, useToast } from "@familyzone/component-library";
import React from "react";
import CardBasedPage from "../../components/templates/CardBasedPage";
import countries from "i18n-iso-countries";
import * as pkijs from "pkijs";
import * as asn1js from "asn1js";
import { buildCertificateSubject, CertificateSubjectData } from "./certificateSubject";
import { Menu, MenuButton, MenuItem, MenuList } from "@chakra-ui/react";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - the locale JSON type is not declared by the library
import enLocale from "i18n-iso-countries/langs/en.json";

countries.registerLocale(enLocale as unknown as { locale: string; countries: Record<string, string> });

const allCountryOptions = (() => {
  const alpha2 = countries.getAlpha2Codes() as Record<string, string>;
  return Object.keys(alpha2)
    .map((code) => {
      const name = countries.getName(code, "en") ?? code;
      return { code, label: `${name} (${code})` };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
})();

const pinnedCountryCodes = ["AU", "NZ", "US", "GB"] as const;

// Minimal utility styles for validation and visibility.
const styles = `
.crypto-assets-root .field-error {
  outline: 2px solid #d00;
}

.crypto-assets-root .hidden {
  display: none;
}

@keyframes crypto-status-pulse {
  0% {
    transform: scale(0.98);
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.08);
  }
  60% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(0, 0, 0, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
  }
}

.crypto-assets-root .crypto-status-pulse {
  animation: crypto-status-pulse 700ms ease-out;
}

.crypto-assets-root .crypto-status-inline > * {
  padding-left: 12px;
  padding-right: 12px;
}
`;

const CryptoAssets: React.FC = () => {
  const { t } = useTranslation();
  const [statusMessage, setStatusMessage] = React.useState<string>("");
  const [lastRootCACertB64, setLastRootCACertB64] = React.useState<string>("");
  const [lastRootCAPrivateKeyB64, setLastRootCAPrivateKeyB64] = React.useState<string>("");
  const { successToast, errorToast } = useToast();
  const title = t("Self-Service Certificates Portal");
  const breadcrumbs = [
    { title: t("Configuration"), url: "/config", isActive: false },
    { title, isActive: true },
  ];
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  const copyJsonPairToClipboard = async (key: string, value: string) => {
    const text = `"${key}": "${value}"`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      successToast({
        title: t("Copied"),
        description: t("Copied {{key}} JSON pair to clipboard", { key }),
        position: "top-right",
        isClosable: true,
      });
    } catch (err) {
      console.error("Clipboard copy failed", err);
      errorToast({
        title: t("Copy failed"),
        description: t("Failed to copy to clipboard"),
        position: "top-right",
        isClosable: true,
      });
    }
  };

  const copyRawValueToClipboard = async (key: string, value: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = value;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      successToast({
        title: t("Copied"),
        description: t("Copied {{key}} value to clipboard", { key }),
        position: "top-right",
        isClosable: true,
      });
    } catch (err) {
      console.error("Clipboard copy failed", err);
      errorToast({
        title: t("Copy failed"),
        description: t("Failed to copy to clipboard"),
        position: "top-right",
        isClosable: true,
      });
    }
  };

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let isMounted = true;
    const cleanupFns: Array<() => void> = [];

    let subtle: SubtleCrypto | null | undefined = null;

    const form = container.querySelector<HTMLFormElement>("#crypto-assets-form");
    const generateBtn = container.querySelector<HTMLButtonElement>("#crypto-generate-btn");
    // Status is now managed via React state instead of direct DOM updates.
    const resultEl = container.querySelector<HTMLDivElement>("#crypto-result");
    const rootCaCertValueEl = container.querySelector<HTMLSpanElement>("#crypto-root-ca-cert-value");
    const rootCaKeyValueEl = container.querySelector<HTMLSpanElement>("#crypto-root-ca-key-value");
    const pemSectionEl = container.querySelector<HTMLDivElement>("#crypto-pem-section");
    const certPemEl = container.querySelector<HTMLPreElement>("#crypto-cert-pem");

    const countryEl = container.querySelector<HTMLSelectElement>("#crypto-country");
    const stateEl = container.querySelector<HTMLInputElement>("#crypto-state");
    const localityEl = container.querySelector<HTMLInputElement>("#crypto-locality");
    const organisationEl = container.querySelector<HTMLInputElement>("#crypto-organisation");
    const commonNameEl = container.querySelector<HTMLInputElement>("#crypto-common-name");
    const daysEl = container.querySelector<HTMLInputElement>("#crypto-valid-days");
    const keySizeEl = container.querySelector<HTMLSelectElement>("#crypto-key-size");

    const setStatus = (msg?: string) => {
      setStatusMessage(msg || "");
    };

    const showFieldError = (element: HTMLElement | null, message: string) => {
      if (!element) {
        setStatus(message);
        return;
      }
      element.classList.add("field-error");
      setStatus(message);
      if ("focus" in element && typeof element.focus === "function") {
        element.focus();
      }
    };

    const clearFieldErrors = () => {
      setStatus("");
      container.querySelectorAll(".field-error").forEach((el) => el.classList.remove("field-error"));
    };

    const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
      const bytes = new Uint8Array(buffer);
      let binary = "";
      const chunkSize = 0x8000;
      for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
      }
      return btoa(binary);
    };

    const toPem = (label: string, base64Body: string) => {
      const lines = base64Body.match(/.{1,64}/g) || [];
      return `-----BEGIN ${label}-----\n${lines.join("\n")}\n-----END ${label}-----`;
    };

    const ensureCryptoEngine = () => {
      const engine = pkijs.getEngine() as unknown as {
        name: string;
        crypto?: (Crypto & { subtle?: SubtleCrypto }) | undefined;
      };
      if (engine.name === "webcrypto") {
        const resolvedSubtle: SubtleCrypto | null = engine.crypto?.subtle ?? null;
        if (!resolvedSubtle) {
          throw new Error("WebCrypto engine is configured but subtle is not available");
        }
        subtle = resolvedSubtle;
        return;
      }
      const crypto = window.crypto;
      if (!crypto?.subtle) {
        throw new Error("WebCrypto not available in this browser");
      }
      pkijs.setEngine("webcrypto", crypto, crypto.subtle);
      subtle = crypto.subtle;
    };

    const generateKeyPair = async (keySize: number) => {
      if (!subtle) {
        throw new Error("WebCrypto subtle engine not initialised");
      }
      return subtle.generateKey(
        {
          name: "RSASSA-PKCS1-v1_5",
          modulusLength: keySize,
          publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
          hash: { name: "SHA-256" },
        },
        true,
        ["sign", "verify"]
      );
    };

    const exportPrivateKeyPkcs8 = async (privateKey: CryptoKey) => {
      if (!subtle) {
        throw new Error("WebCrypto subtle engine not initialised");
      }
      return subtle.exportKey("pkcs8", privateKey);
    };

    const randomSerialNumber = () => {
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      bytes[0] &= 0x7f;
      return new asn1js.Integer({ valueHex: bytes.buffer });
    };

    const createSelfSignedCACert = async (subjectData: CertificateSubjectData, days: number, keyPair: CryptoKeyPair) => {
      const { Certificate, Extension, BasicConstraints } = pkijs;
      const cert = new Certificate();
      cert.subject = buildCertificateSubject(subjectData);
      cert.issuer = cert.subject;

      // pkijs expects an ASN.1 Integer here; randomSerialNumber returns asn1js.Integer from the top-level asn1js package.
      // At runtime these are compatible, but their TypeScript types come from different module paths, so we cast through `unknown`.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
      (cert as unknown as { serialNumber: unknown }).serialNumber = randomSerialNumber();

      const notBefore = new Date();
      const notAfter = new Date(notBefore.getTime() + days * 24 * 60 * 60 * 1000);
      cert.notBefore.value = notBefore;
      cert.notAfter.value = notAfter;

      cert.version = 2;
      await cert.subjectPublicKeyInfo.importKey(keyPair.publicKey);

      const basicConstraints = new BasicConstraints({ cA: true });
      const keyUsageBits = new ArrayBuffer(1);
      const keyUsageView = new Uint8Array(keyUsageBits);
      keyUsageView[0] = 0x06; // keyCertSign (bit 5, 0x04) | cRLSign (bit 6, 0x02) — RFC 5280 MSB-first bit ordering

      cert.extensions = [
        new Extension({
          extnID: "2.5.29.19",
          critical: true,
          extnValue: basicConstraints.toSchema().toBER(false),
        }),
        new Extension({
          extnID: "2.5.29.15",
          critical: true,
          extnValue: new asn1js.BitString({ valueHex: keyUsageBits }).toBER(false),
        }),
      ];

      await cert.sign(keyPair.privateKey, "SHA-256");

      return cert;
    };

    const generateCA = async (event: Event) => {
      event.preventDefault();

      if (!form || !certPemEl || !pemSectionEl || !resultEl || !generateBtn) {
        return;
      }

      resultEl.classList.add("hidden");
      if (rootCaCertValueEl) rootCaCertValueEl.textContent = "";
      if (rootCaKeyValueEl) rootCaKeyValueEl.textContent = "";
      setLastRootCACertB64("");
      setLastRootCAPrivateKeyB64("");
      certPemEl.textContent = "";
      pemSectionEl.classList.add("hidden");

      try {
        ensureCryptoEngine();
        clearFieldErrors();

        const c = countryEl?.value.trim() ?? "";
        const st = stateEl?.value.trim() ?? "";
        const l = localityEl?.value.trim() ?? "";
        const o = organisationEl?.value.trim() ?? "";
        const cn = commonNameEl?.value.trim() ?? "";
        const days = parseInt(daysEl?.value ?? "0", 10) || 0;
        const keySize = parseInt(keySizeEl?.value ?? "2048", 10) || 2048;

        const countryRegex = /^[A-Z]{2}$/;
        if (!countryRegex.test(c)) {
          showFieldError(countryEl, t("Country (C) must be a 2-letter ISO country code (A–Z)."));
          return;
        }
        if (!st) {
          showFieldError(stateEl, t("State / Province (ST) is required."));
          return;
        }
        if (!l) {
          showFieldError(localityEl, t("Locality (L) is required."));
          return;
        }
        if (!o) {
          showFieldError(organisationEl, t("Organisation (O) is required."));
          return;
        }
        if (!cn) {
          showFieldError(commonNameEl, t("Common Name (CN) is required."));
          return;
        }
        if (days <= 0 || days > 7300) {
          showFieldError(daysEl, t("Validity must be between 1 and 7300 days."));
          return;
        }
        if (![2048, 3072, 4096].includes(keySize)) {
          showFieldError(keySizeEl, t("Key size must be one of: 2048, 3072, 4096."));
          return;
        }

        generateBtn.disabled = true;
        setStatus(t("Generating key pair..."));

        const keyPair = await generateKeyPair(keySize);

        setStatus(t("Creating self-signed CA certificate..."));

        const cert = await createSelfSignedCACert({ c, st, l, o, cn }, days, keyPair);

        const certSchema = cert.toSchema(true);
        const certDer = certSchema.toBER(false);
        const certDerBase64 = arrayBufferToBase64(certDer);
        const certPem = toPem("CERTIFICATE", certDerBase64);

        setStatus(t("Exporting private key..."));
        const pkcs8Der = await exportPrivateKeyPkcs8(keyPair.privateKey);
        const pkcs8Base64 = arrayBufferToBase64(pkcs8Der);
        const keyPem = toPem("PRIVATE KEY", pkcs8Base64);

        certPemEl.textContent = certPem;
        pemSectionEl.classList.remove("hidden");

        const blob = new Blob([certPem], { type: "application/x-pem-file" });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = "root-ca-cert.cer";
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);

        const certPemB64 = btoa(certPem);
        const keyPemB64 = btoa(keyPem);
        setLastRootCACertB64(certPemB64);
        setLastRootCAPrivateKeyB64(keyPemB64);

        if (rootCaCertValueEl) rootCaCertValueEl.textContent = `"${certPemB64}"`;
        if (rootCaKeyValueEl) rootCaKeyValueEl.textContent = `"${keyPemB64}"`;

        resultEl.classList.remove("hidden");
        setStatus(t("Done. Certificate generated and downloaded."));
      } catch (err: unknown) {
        console.error(err);
        const message = err instanceof Error ? err.message : String(err);
        setStatus(`Error: ${message}`);
      } finally {
        if (generateBtn) {
          generateBtn.disabled = false;
        }
      }
    };

    const init = () => {
      try {
        if (!isMounted || !form) {
          return;
        }

        const submitHandler = (event: Event) => void generateCA(event);
        form.addEventListener("submit", submitHandler);
        cleanupFns.push(() => form.removeEventListener("submit", submitHandler));

        // Primary "Options" buttons no longer perform the copy directly; copies are triggered
        // immediately when the user selects a specific option from the dropdown menu.
      } catch (error) {
        console.error("Failed to initialise Crypto Assets page", error);
      }
    };

    void init();

    return () => {
      isMounted = false;
      cleanupFns.forEach((fn) => fn());
    };
  }, [t]);

  return (
    <CardBasedPage title={title} breadcrumbs={breadcrumbs}>
      <style>{styles}</style>
      <Box className="crypto-assets-root" ref={containerRef} p="sp24">
        <Box mb="sp24">
          <InlineNotification
            status="info"
            isRemovable={false}
            notificationTitle=""
            notificationDescription={t(
              "Generation of sensitive materials happens entirely in your browser. No data is sent over the network, and nothing is persisted on Linewize servers."
            )}
          />
        </Box>

        <Box width="100%" mb="sp24">
          <Text fontSize="sm" color="text.paragraph.regular" mb="sp4">
            {t(
              "Use this page to generate a self-signed root certificate and private key for deploying trusted HTTPS interception on managed devices."
            )}
          </Text>
          <Text fontSize="sm" color="text.paragraph.regular">
            {t(
              "Fill in the certificate subject, choose an appropriate key size and validity period, then select Generate. Download and distribute the generated root certificate and provisioning values using your MDM tooling."
            )}
          </Text>
        </Box>

        <Box as="form" id="crypto-assets-form" width="100%">
          <Box mb="sp24">
            <Text as="h2" fontFamily="Poppins" fontSize="md" fontWeight="medium" mb="sp8">
              {t("Self-Signed Root Certificate Subject")}
            </Text>
            <Box as="label" display="block" mt="sp12">
              <Text as="span" display="block" mb="sp4">
                {t("Country (C)")}
              </Text>
              <Box
                as="select"
                id="crypto-country"
                defaultValue="AU"
                required
                width="100%"
                p="sp8"
                borderWidth="1px"
                borderRadius="sm"
                borderColor="neutrals.40"
              >
                {/* Pinned common countries at the top */}
                {pinnedCountryCodes.map((code) => {
                  const opt = allCountryOptions.find((c) => c.code === code);
                  if (!opt) return null;
                  return (
                    <option key={opt.code} value={opt.code}>
                      {opt.label}
                    </option>
                  );
                })}
                <option disabled>──────────</option>
                {/* Remaining countries in alphabetical order */}
                {allCountryOptions
                  .filter((c) => !pinnedCountryCodes.includes(c.code as (typeof pinnedCountryCodes)[number]))
                  .map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
              </Box>
            </Box>
            <Box as="label" display="block" mt="sp12">
              <Text as="span" display="block" mb="sp4">
                {t("State / Province (ST)")}
              </Text>
              <Box
                as="input"
                type="text"
                id="crypto-state"
                placeholder="e.g. WA"
                required
                width="100%"
                p="sp8"
                borderWidth="1px"
                borderRadius="sm"
                borderColor="neutrals.40"
              />
            </Box>
            <Box as="label" display="block" mt="sp12">
              <Text as="span" display="block" mb="sp4">
                {t("Locality (L)")}
              </Text>
              <Box
                as="input"
                type="text"
                id="crypto-locality"
                placeholder="e.g. Perth"
                required
                width="100%"
                p="sp8"
                borderWidth="1px"
                borderRadius="sm"
                borderColor="neutrals.40"
              />
            </Box>
            <Box as="label" display="block" mt="sp12">
              <Text as="span" display="block" mb="sp4">
                {t("Organisation (O)")}
              </Text>
              <Box
                as="input"
                type="text"
                id="crypto-organisation"
                placeholder="e.g. School Name"
                required
                width="100%"
                p="sp8"
                borderWidth="1px"
                borderRadius="sm"
                borderColor="neutrals.40"
              />
            </Box>
            <Box as="label" display="block" mt="sp12">
              <Text as="span" display="block" mb="sp4">
                {t("Common Name (CN)")}
              </Text>
              <Box
                as="input"
                type="text"
                id="crypto-common-name"
                placeholder="e.g. school.edu.au"
                required
                width="100%"
                p="sp8"
                borderWidth="1px"
                borderRadius="sm"
                borderColor="neutrals.40"
              />
            </Box>
          </Box>

          <Box mb="sp24">
            <Text as="h2" fontFamily="Poppins" fontSize="md" fontWeight="medium" mb="sp8">
              {t("Key options")}
            </Text>
            <Box as="label" display="block" mt="sp12">
              <Text as="span" display="block" mb="sp4">
                {t("Key size (bits)")}
              </Text>
              <Box
                as="select"
                id="crypto-key-size"
                defaultValue="2048"
                required
                width="100%"
                p="sp8"
                borderWidth="1px"
                borderRadius="sm"
                borderColor="neutrals.40"
              >
                <option value="2048">2048</option>
                <option value="3072">3072</option>
                <option value="4096">4096</option>
              </Box>
            </Box>
            <Box as="label" display="block" mt="sp12">
              <Text as="span" display="block" mb="sp4">
                {t("Valid for (days)")}
              </Text>
              <Box
                as="input"
                type="number"
                id="crypto-valid-days"
                defaultValue="2190"
                min="365"
                max="7300"
                required
                width="100%"
                p="sp8"
                borderWidth="1px"
                borderRadius="sm"
                borderColor="neutrals.40"
              />
            </Box>
            <Text mt="sp8" fontSize="xs" color="text.paragraph.subtle">
              {t("Recommended range: 365–7300 days (up to 20 years).")}
            </Text>
          </Box>

          <Flex alignItems="center" mt="sp12" columnGap="sp12" rowGap="sp8" flexWrap="wrap">
            <Button type="submit" id="crypto-generate-btn" variant="primary">
              {t("Generate")}
            </Button>
            {statusMessage && (
              <Box
                key={statusMessage}
                aria-live="polite"
                display="inline-flex"
                flexShrink={1}
                maxW="480px"
                px="sp8"
                py="sp4"
                className="crypto-status-pulse crypto-status-inline"
              >
                <InlineNotification
                  status={(() => {
                    if (statusMessage.startsWith("Error:")) return "error" as const;
                    if (statusMessage.toLowerCase().includes("failed")) return "error" as const;
                    if (statusMessage.startsWith(t("Done."))) return "success" as const;
                    if (statusMessage === t("Choose a copy option first")) return "warning" as const;
                    return "info" as const;
                  })()}
                  isRemovable={false}
                  notificationTitle={statusMessage}
                  notificationDescription=""
                />
              </Box>
            )}
          </Flex>
        </Box>

        <Box id="crypto-result" className="hidden" mt="sp24" width="100%" data-testid="crypto-result">
          <Text as="h2" fontFamily="Poppins" fontSize="lg" fontWeight="medium" mb="sp8">
            {t("Provisioning values")}
          </Text>
          <Text mb="sp12" fontSize="sm" color="text.paragraph.regular">
            {t(
              "Use these JSON fields in your MDM user provisioning. Values are truncated for display but the buttons copy the full values."
            )}
          </Text>

          <Box mb="sp8">
            <Flex alignItems="center">
              <Text as="span" fontFamily="mono" whiteSpace="nowrap" mr="sp8">
                "SigningCertificate":
              </Text>
              <Box
                as="span"
                id="crypto-root-ca-cert-value"
                flex="1"
                minW={0}
                fontFamily="mono"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                title="SigningCertificate value"
              />
              <Menu isLazy>
                <MenuButton
                  as={Button}
                  ml="sp8"
                  size="sm"
                  variant="secondary"
                  aria-label={t("More copy options")}
                  display="inline-flex"
                  alignItems="center"
                >
                  <Flex>
                    <Box
                      as="span"
                      px="sp12"
                      py="sp4"
                      borderTopLeftRadius="sm"
                      borderBottomLeftRadius="sm"
                      borderRightWidth="1px"
                      borderColor="neutrals.40"
                    >
                      {t("Options")}
                    </Box>
                    <Box as="span" px="sp8" py="sp4" borderTopRightRadius="sm" borderBottomRightRadius="sm">
                      <Icon icon="fa-chevron-down" />
                    </Box>
                  </Flex>
                </MenuButton>
                <MenuList zIndex={20}>
                  <MenuItem
                    as="button"
                    onClick={() => {
                      if (!lastRootCACertB64) return;
                      void copyJsonPairToClipboard("SigningCertificate", lastRootCACertB64);
                    }}
                  >
                    {t("Copy JSON")}
                  </MenuItem>
                  <MenuItem
                    as="button"
                    id="crypto-copy-root-ca-cert-raw"
                    onClick={() => {
                      if (!lastRootCACertB64) return;
                      void copyRawValueToClipboard("SigningCertificate", lastRootCACertB64);
                    }}
                  >
                    {t("Copy value")}
                  </MenuItem>
                </MenuList>
              </Menu>
            </Flex>
          </Box>

          <Box mb="sp8">
            <Flex alignItems="center">
              <Text as="span" fontFamily="mono" whiteSpace="nowrap" mr="sp8">
                "SigningCertificateKey":
              </Text>
              <Box
                as="span"
                id="crypto-root-ca-key-value"
                flex="1"
                minW={0}
                fontFamily="mono"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                title="SigningCertificateKey value"
              />
              <Menu isLazy>
                <MenuButton
                  as={Button}
                  ml="sp8"
                  size="sm"
                  variant="secondary"
                  aria-label={t("More copy options")}
                  display="inline-flex"
                  alignItems="center"
                >
                  <Flex>
                    <Box
                      as="span"
                      px="sp12"
                      py="sp4"
                      borderTopLeftRadius="sm"
                      borderBottomLeftRadius="sm"
                      borderRightWidth="1px"
                      borderColor="neutrals.40"
                    >
                      {t("Options")}
                    </Box>
                    <Box as="span" px="sp8" py="sp4" borderTopRightRadius="sm" borderBottomRightRadius="sm">
                      <Icon icon="fa-chevron-down" />
                    </Box>
                  </Flex>
                </MenuButton>
                <MenuList zIndex={20}>
                  <MenuItem
                    as="button"
                    onClick={() => {
                      if (!lastRootCAPrivateKeyB64) return;
                      void copyJsonPairToClipboard("SigningCertificateKey", lastRootCAPrivateKeyB64);
                    }}
                  >
                    {t("Copy JSON")}
                  </MenuItem>
                  <MenuItem
                    as="button"
                    id="crypto-copy-root-ca-key-raw"
                    onClick={() => {
                      if (!lastRootCAPrivateKeyB64) return;
                      void copyRawValueToClipboard("SigningCertificateKey", lastRootCAPrivateKeyB64);
                    }}
                  >
                    {t("Copy value")}
                  </MenuItem>
                </MenuList>
              </Menu>
            </Flex>
          </Box>

          <Box id="crypto-pem-section" className="hidden" mt="sp16">
            <Text as="h3" fontFamily="Poppins" fontSize="md" fontWeight="medium" mb="sp4">
              {t("Root certificate (PEM)")}
            </Text>
            <Text mb="sp8" fontSize="sm" color="text.paragraph.regular">
              {t(
                "This file has also been downloaded as root-ca-cert.cer. This should be installed as a trusted root certificate on protected devices using your MDM."
              )}
            </Text>
            <Box
              as="pre"
              id="crypto-cert-pem"
              fontSize="xs"
              whiteSpace="pre-wrap"
              wordBreak="break-all"
              p="sp8"
              borderWidth="1px"
              borderRadius="sm"
              borderColor="neutrals.40"
              backgroundColor="neutrals.10"
            />
          </Box>
        </Box>
      </Box>
    </CardBasedPage>
  );
};

export default CryptoAssets;
